/* eslint-disable indent */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { saveAs } from 'file-saver';
import {
  Flex,
  Typography,
  Chip,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
} from '@dripcapital/dripui-v1';
import { BorderCardContainer, Spacer, Modal, AtomicButton, Tab } from 'atoms';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { appDataVar } from 'config/server';
import { ButtonTypeEnum, EsignStatusMXFinancial } from 'enums';
import { File } from 'api';
import { DAJ_COLORS, STYLES } from 'utils';

type Props = {
  showRightButtons?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  moreDetailsHandler?: React.MouseEventHandler<HTMLAnchorElement>;
  paymentTerms?: string;
  filename?: string;
  filenameLabel?: string;
  chipLabel?: string;
  roles?: string[];
  contentSection?: React.ReactNode;
  esignStatus?: EsignStatusMXFinancial;
  showCustomerEsignButton?: boolean;
  esignHandler?: () => void;
  title?: string;
  file?: File;
  name?: string;
};

const BusinessInfoCard: React.FC<Props> = ({
  showRightButtons = false,
  onEdit,
  onDelete,
  filename,
  filenameLabel,
  title = '',
  chipLabel = '',
  roles,
  contentSection,
  esignStatus,
  esignHandler,
  showCustomerEsignButton = false,
  file,
  name = '',
}: Props) => {
  const { t } = useTranslation();

  const { isBDMVar: isBDM } = useReactiveVar(appDataVar);

  const theme = useTheme();

  const isTab = useMediaQuery(theme.breakpoints.down('md'));

  const [removeShareholder, setRemoveShareholder] =
    React.useState<boolean>(false);

  const closeDialog = () => setRemoveShareholder(false);

  const deleteShareholder = () => {
    // TODO: no need to delete the card right now as graphql state will handle it on its own
    onDelete?.();
    closeDialog();
  };

  const handleDownloadFile = () => {
    saveAs(
      file?.url ?? '',
      t('mrfi_download_report', { fileName: file?.name })
    );
  };

  const renderCountryInChip = () => {
    return (
      <Chip
        label={chipLabel}
        startIcon={<LocationOnOutlinedIcon fontSize="small" />}
        size="small"
      />
    );
  };

  const renderDownloadButton = () => {
    return (
      <Grid xs={3} _sx={{ justifyContent: 'end' }} item>
        <Tab
          label=""
          buttonLabel={isTab ? '' : t('btn_download')}
          Icon={<DownloadIcon fontSize={isTab ? 'medium' : 'small'} />}
          callback={handleDownloadFile}
        />
      </Grid>
    );
  };

  const renderRightButtons = () => {
    return (
      <>
        <Grid xs="auto" alignItems="flex-end" item>
          {onEdit && (
            <AtomicButton
              dataTestId="tab-button-element"
              variant={ButtonTypeEnum.TEXT}
              endIcon={EditOutlinedIcon}
              onClick={() => onEdit?.()}
              type={'button'}
              _sx={{
                color: DAJ_COLORS.WEALTHY_GREEN,
                fontSize: 16,
                lineHeight: 1,
                padding: 0.4,
                '.MuiButton-startIcon': {
                  marginRight: 0.2,
                },
              }}
            >
              {t('edit_item')}
            </AtomicButton>
          )}
        </Grid>
        <Grid xs="auto" alignItems="flex-end" item>
          {onDelete && (
            <AtomicButton
              dataTestId="tab-button-element"
              variant={ButtonTypeEnum.TEXT}
              endIcon={DeleteOutlineIcon}
              onClick={() => setRemoveShareholder?.(true)}
              type={'button'}
              _sx={{
                color: DAJ_COLORS.WEALTHY_GREEN,
                fontSize: 16,
                lineHeight: 1,
                padding: 0.4,
                '.MuiButton-startIcon': {
                  marginRight: 0.2,
                },
              }}
            >
              {t('delete_item')}
            </AtomicButton>
          )}
        </Grid>
      </>
    );
  };

  // TODO: Could be used in future.
  // const renderMoreDetails = () => {
  //   return (
  //     <Grid xs={12} md={3} item>
  //       <Flex alignItems={'center'}>
  //         <Link
  //           underline="none"
  //           _sx={{
  //             fontSize: FONT.SIZES.F16,
  //             marginRight: 1,
  //             marginLeft: 'auto',
  //           }}
  //           onClick={moreDetailsHandler}
  //           component={'button'}
  //           color="gray"
  //         >
  //           {t('title_more_details')}
  //         </Link>
  //         <OpenInNew fontSize="small" color="disabled" />
  //       </Flex>
  //     </Grid>
  //   );
  // };

  const renderUploadFileName = () => {
    return (
      <Flex alignItems={'center'} _sx={{ marginTop: 2 }}>
        {filenameLabel && (
          <Typography variant="body">
            {t('phrase_purchase_order_doc')}
          </Typography>
        )}
        <FolderOpenIcon style={{ margin: '0 10px' }} />
        <Typography variant="body">{filename}</Typography>
      </Flex>
    );
  };

  const renderEsignButton = () => {
    return (
      <>
        <Spacer verticalSpace={2} />
        <AtomicButton
          variant={ButtonTypeEnum.OUTLINED}
          onClick={() => esignHandler?.()}
        >
          {t('label_continue_e_sign')}
        </AtomicButton>
        <Spacer verticalSpace={1} />
      </>
    );
  };

  const renderEsignSection = () => {
    return (
      <Box>
        {contentSection}
        {esignStatus && (
          <>
            <Spacer verticalSpace={3} />
            <Flex spacing={1}>
              {esignStatus === EsignStatusMXFinancial.PENDING &&
                !showCustomerEsignButton && (
                  <>
                    <ErrorIcon />
                    <Box>
                      <Typography fontWeight="bold">
                        {t('phrase_customer_esigns')}
                      </Typography>
                      <Typography>
                        {t('phrase_customer_esigns_subtext', { name })}
                      </Typography>
                    </Box>
                  </>
                )}
              {esignStatus === EsignStatusMXFinancial.COMPLETE && (
                <>
                  <CheckCircleIcon color="success" />
                  <Typography>{t('label_auth_letter', { name })}</Typography>
                </>
              )}
            </Flex>
          </>
        )}
        {showCustomerEsignButton &&
          !isBDM &&
          esignHandler &&
          esignStatus !== EsignStatusMXFinancial.COMPLETE &&
          renderEsignButton()}
      </Box>
    );
  };

  return (
    <BorderCardContainer dataTestId="business-info-card">
      <Modal
        dataTestId="business-info-modal"
        isOpen={removeShareholder}
        closeDialog={closeDialog}
        title={'Are you sure you want to delete?'}
        content={'Once deleted, data cannot be recovered'}
        buttons={[
          {
            dataTestId: 'accept-button',
            title: t('btn_delete'),
            handler: deleteShareholder,
            variant: ButtonTypeEnum.CONTAINED,
            _sx: { padding: theme.spacing(1.3) },
          },
          {
            dataTestId: 'deny-button',
            title: t('btn_cancel'),
            handler: closeDialog,
            variant: ButtonTypeEnum.OUTLINED,
            _sx: { padding: theme.spacing(1.3) },
          },
        ]}
        _sx={{ padding: theme.spacing(3) }}
      />
      <Grid container rowSpacing={2}>
        <Grid xs={10} item>
          <Flex spacing={1} _sx={{ height: 32, paddingTop: '8px' }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              color="secondary"
              _sx={STYLES.textOverflow}
              title={title}
              align="center"
              noWrap
            >
              {title}
            </Typography>
            {chipLabel && renderCountryInChip()}
          </Flex>
          {roles && <Spacer verticalSpace={2} />}
          {roles &&
            roles.map((role, index) => (
              <Chip key={index} label={t(`${role}`)} />
            ))}
        </Grid>
        {esignStatus === EsignStatusMXFinancial.COMPLETE &&
          renderDownloadButton()}
        {showRightButtons && renderRightButtons()}
        {/* {moreDetailsHandler && renderMoreDetails()} */}
      </Grid>
      {filename ? renderUploadFileName() : renderEsignSection()}
    </BorderCardContainer>
  );
};

export default BusinessInfoCard;
